import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Ticket, Plus, Calendar as CalendarIcon, Trash2, Search, CheckSquare, ListChecks } from 'lucide-react';
import apiService from '../services/apiService';
import { CalenderMarkerHeaderSelect } from '../utils/calenderMarkerUtils';
import { useI18n } from '../utils/i18n';
import Modal from './ui/Modal';
import { useToast } from './ToastProvider';
import { useConfirm } from './ui/ConfirmDialog';
import TimeblockChecklist from './TimeblockChecklist';
import TicketCustomerField from './TicketCustomerField';

// Vorlagen sind seit #338 serverseitig Manager-Sache (requireManager auf allen
// fuenf schreibenden /api/templates-Routen). Die Knoepfe dafuer blenden wir
// entsprechend aus -- ein Knopf, der zuverlaessig 403 liefert, ist die Lehre
// aus #185. Anwenden bleibt offen: daraus entsteht ein Vorgang, das darf jeder
// Ticketworker.
const useIstManager = () => {
  const [istManager, setIstManager] = useState(false);
  useEffect(() => {
    let abgebrochen = false;
    apiService.getUser()
      .then((u) => { if (!abgebrochen) setIstManager(!!(u?.dbUser?.isManager)); })
      .catch(() => { if (!abgebrochen) setIstManager(false); });
    return () => { abgebrochen = true; };
  }, []);
  return istManager;
};

// Hilfsfunktion: Entfernt bekannte Präfixe (Projekt- und/oder Teilvorgangsname) aus einem Ticket-Titel
const stripKnownPrefixes = (rawTitle, projectName, subprojectName) => {
  try {
    let title = String(rawTitle || '');
    const safe = (s) => (s || '').toString();
    const proj = safe(projectName);
    const sub = safe(subprojectName);
    const join = (...parts) => parts.filter(p => p && p.length > 0).join(' - ');
    const tryStrip = (prefix) => {
      const p = safe(prefix);
      if (!p) return null;
      const marker = p + ' - ';
      if (title.startsWith(marker)) return title.slice(marker.length);
      return null;
    };

    // Versuche vollständiges Präfix: "Projekt - Teilvorgang - "
    const fullPrefix = join(proj, sub);
    let stripped = tryStrip(fullPrefix);
    if (stripped !== null) return stripped.trim();

    // Nur Teilvorgang
    stripped = tryStrip(sub);
    if (stripped !== null) return stripped.trim();

    // Nur Projekt
    stripped = tryStrip(proj);
    if (stripped !== null) return stripped.trim();

    // Fallback: entferne erstes Segment
    return title.replace(/^.*?-\s*/, '').trim();
  } catch (_) {
    return (rawTitle || '');
  }
};

// Hilfsfunktion: Verhindert doppeltes Präfix beim Titelaufbau
const buildTitleWithPrefix = (prefix, suffix) => {
  const p = (prefix || '').trim();
  const s = (suffix || '').trim();
  if (!p) return s;
  if (!s) return p; // nur Präfix
  // Wenn Suffix bereits mit "Prefix - " beginnt, nicht erneut präfixen
  if (s.startsWith(p + ' - ')) return s;
  // Wenn Suffix exakt mit Prefix beginnt (inkl. Varianten mit Klammern), nicht erneut präfixen
  if (s === p || s.startsWith(p + ' (')) return s;
  return `${p} - ${s}`;
};

const CreateProjectWizard = ({ onClose, onCreated, initialMode = null, initialData = null, readOnly = false }) => {
  const istManager = useIstManager();
  const { t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState([]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [responsibleEmail, setResponsibleEmail] = useState('');
  // #103: Kundenzuordnung des Vorgangs (nur Projekt-Ebene)
  const [customerId, setCustomerId] = useState(null);
  // Turnus Einstellungen
  const [turnusCount, setTurnusCount] = useState('');
  const [turnusEvery, setTurnusEvery] = useState('');
  const [turnusUnit, setTurnusUnit] = useState('days');
  const [hasSubprojects, setHasSubprojects] = useState(false);
  const [subprojects, setSubprojects] = useState([{ name: '' }]);

  // For timeblocks
  const [timeblocks, setTimeblocks] = useState([{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '', calenderMarker: '', checklist: [] }]);
  const [initialBlocks, setInitialBlocks] = useState([]); // for diff (project or single subproject)

  // For subproject definitions (descriptions and responsibles)
  const [subprojectDetails, setSubprojectDetails] = useState([]);
  const [showSubTimeblocksPanel, setShowSubTimeblocksPanel] = useState(false);
  const [activeSubproject, setActiveSubproject] = useState(null); // { id?: number|null, name: string, index?: number }
  const [subprojectTimeblocks, setSubprojectTimeblocks] = useState([]); // Array of lists for creation flow
  const appliedTemplateIdRef = useRef(null);
  // Frage-Modal fr Vorlage aktualisieren vs. neu erstellen
  const [templateChoiceModal, setTemplateChoiceModal] = useState({ visible: false, payload: null });
  const [templateChoiceSaving, setTemplateChoiceSaving] = useState(false);

  // Sperre: Beim Überarbeiten eines bestehenden Projekts darf der Subprojekt-Umschalter nicht geändert werden
  const isEditingProject = (initialMode === 'project' && !!(initialData?.id));
  const subprojectsToggleDisabled = readOnly || isEditingProject;
  const turnusDisabled = readOnly || isEditingProject;
  const isCreatingNew = !initialMode && !readOnly;
  const [showTemplatesPanel, setShowTemplatesPanel] = useState(isCreatingNew);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiService.getCalendarUsers();
        setUsers(res.users || []);
      } catch (e) {
        console.error('Error loading users', e);
      }
    })();
  }, []);

  // Hide templates panel once the wizard proceeds beyond step 1 (safety net)
  useEffect(() => {
    if (step > 1 && showTemplatesPanel) {
      setShowTemplatesPanel(false);
    }
  }, [step, showTemplatesPanel]);

  // Show templates panel again when returning to step 1 during creation flow
  useEffect(() => {
    if (!readOnly && isCreatingNew && step === 1 && !showTemplatesPanel) {
      setShowTemplatesPanel(true);
    }
  }, [step, isCreatingNew, readOnly, showTemplatesPanel]);

  // Prefill from initial data (project or subproject)
  useEffect(() => {
    (async () => {
      if (!initialData) return;
      if (initialMode === 'project') {
        setName(initialData.name || '');
        setDescription(initialData.description || '');
        setResponsibleEmail(initialData.responsibleEmail || '');
        setCustomerId(initialData.customerId || null);
        setTurnusCount(initialData.turnusCount != null ? String(initialData.turnusCount) : '');
        setTurnusEvery(initialData.turnusEvery != null ? String(initialData.turnusEvery) : '');
        setTurnusUnit(initialData.turnusUnit || 'days');
        const hasSubs = Array.isArray(initialData.subprojects) && initialData.subprojects.length > 0;
        setHasSubprojects(hasSubs);
        setStep(1);
        if (hasSubs) {
          // Prefill subprojects lists (preserve ids for updates)
          setSubprojects(initialData.subprojects.map(sp => ({ id: sp.id, name: sp.name || '' })));
          setSubprojectDetails(initialData.subprojects.map(sp => ({ description: sp.description || '', responsibleEmail: sp.responsibleEmail || '' })));
        }

        // Wenn kein bestehendes Projekt editiert wird und Timeblocks in initialData übergeben wurden,
        // dann diese direkt als Step-3-Liste übernehmen (nur Projekt ohne Untervorgänge)
        if (!initialData.id && !hasSubs) {
          try {
            const blocks = Array.isArray(initialData.timeblocks) ? initialData.timeblocks : [];
            if (blocks.length > 0) {
              const normalized = blocks.map(tb => ({
                titleSuffix: (tb.titleSuffix || ''),
                description: tb.description || '',
                assignedTo: tb.assignedTo || '',
                plannedHours: (tb.plannedHours != null ? String(tb.plannedHours) : ''),
                calenderMarker: tb.calenderMarker || ''
              }));
              setTimeblocks(normalized);
            }
          } catch (_) {}
        }
        // Load existing project timeblocks (tickets) NUR wenn ein bestehendes Projekt editiert wird
        if (initialData.id) {
          try {
            const res = await apiService.getTickets({ projectId: initialData.id });
            const list = (res.tickets || []).map(t => ({
              id: t.id,
              titleSuffix: stripKnownPrefixes(t.title, (initialData?.name || ''), ''),
              description: t.description || '',
              assignedTo: t.assignedTo || '',
              plannedHours: (t.plannedHours != null ? String(t.plannedHours) : ''),
              calenderMarker: t.calenderMarker || ''
            }));
            setInitialBlocks(list);
            if (list.length > 0) setTimeblocks(list);
          } catch(_) {}
        }
      } else if (initialMode === 'subproject') {
        setName(initialData.name || '');
        setHasSubprojects(true);
        setSubprojects([{ name: initialData.name || '' }]);
        setSubprojectDetails([{ description: initialData.description || '', responsibleEmail: initialData.responsibleEmail || '' }]);
        setTurnusCount(initialData.turnusCount != null ? String(initialData.turnusCount) : '');
        setTurnusEvery(initialData.turnusEvery != null ? String(initialData.turnusEvery) : '');
        setTurnusUnit(initialData.turnusUnit || 'days');
        setStep(2);
        // Load existing subproject timeblocks NUR wenn ein bestehendes Subprojekt editiert wird
        if (initialData.id) {
          try {
            const res = await apiService.getTickets({ subprojectId: initialData.id });
            const list = (res.tickets || []).map(t => ({
              id: t.id,
              titleSuffix: stripKnownPrefixes(t.title, (initialData?.project?.name || ''), (initialData?.name || '')),
              description: t.description || '',
              assignedTo: t.assignedTo || '',
              plannedHours: (t.plannedHours != null ? String(t.plannedHours) : ''),
              calenderMarker: t.calenderMarker || ''
            }));
            setInitialBlocks(list);
            setTimeblocks([{ list }]);
          } catch(_) {}
        }
      }
    })();
  }, [initialMode, initialData]);

  const canContinueStep1 = useMemo(() => {
    // Schritt 1 prüft: Nur Projektname muss gesetzt sein (Projektverantwortlicher optional)
    return !!name.trim();
  }, [name]);

  // Validierungen: Alle "Zugewiesen an" Felder müssen gesetzt sein (für definierte Zeitblöcke)
  const isProjectBlocksAssigneesValid = useMemo(() => {
    if (readOnly) return true;
    return (timeblocks || []).every(tb => {
      const hasContent = ((tb.titleSuffix || '').trim().length > 0) || ((tb.description || '').trim().length > 0);
      if (!hasContent) return true; // leere Zeile ignorieren
      return (tb.assignedTo || '').trim().length > 0;
    });
  }, [timeblocks, readOnly]);

  const isSubprojectBlocksAssigneesValid = useMemo(() => {
    if (readOnly) return true;
    return (subprojects || []).every((_, spIdx) => {
      const list = (timeblocks?.[spIdx]?.list || []);
      return list.every(tb => {
        const hasContent = ((tb.titleSuffix || '').trim().length > 0) || ((tb.description || '').trim().length > 0);
        if (!hasContent) return true;
        return (tb.assignedTo || '').trim().length > 0;
      });
    });
  }, [timeblocks, subprojects, readOnly]);

  const updateSubprojectName = (idx, value) => {
    const next = [...subprojects];
    next[idx].name = value;
    setSubprojects(next);
  };

  const addTimeblock = () => setTimeblocks([...timeblocks, { titleSuffix: '', description: '', assignedTo: '', plannedHours: '', calenderMarker: '', checklist: [] }]);
  const updateTimeblock = (idx, patch) => {
    const next = [...timeblocks];
    next[idx] = { ...next[idx], ...patch };
    setTimeblocks(next);
  };
  const removeTimeblock = async (idx) => {
    if (!(await confirm({ message: t('projectWizard.confirm.deleteTimeblock', 'Diesen Zeitblock löschen?'), danger: true }))) return;
    setTimeblocks(prev => prev.filter((_, i) => i !== idx));
  };

  // Subproject row helpers (Step 2 UI)
  const insertSubprojectAt = (idx) => {
    const nextSubs = [...subprojects];
    const nextDetails = [...subprojectDetails];
    nextSubs.splice(idx + 1, 0, { name: '' });
    nextDetails.splice(idx + 1, 0, { description: '', responsibleEmail: '' });
    setSubprojects(nextSubs);
    setSubprojectDetails(nextDetails);
    const nextBlocks = [...subprojectTimeblocks];
    nextBlocks.splice((idx + 1), 0, []);
    setSubprojectTimeblocks(nextBlocks);
  };

  const removeSubprojectAt = async (idx) => {
    if (!(await confirm({ message: t('projectWizard.confirm.deleteSubproject', 'Diesen Teilvorgang löschen?'), danger: true }))) return;
    const nextSubs = subprojects.filter((_, i) => i !== idx);
    const nextDetails = subprojectDetails.filter((_, i) => i !== idx);
    setSubprojects(nextSubs);
    setSubprojectDetails(nextDetails);
    const nextBlocks = subprojectTimeblocks.filter((_, i) => i !== idx);
    setSubprojectTimeblocks(nextBlocks);
  };

  const createProject = async () => {
    setLoading(true);
    try {
      const payload = {
        name,
        description,
        responsibleEmail: responsibleEmail || null,
        customerId: customerId || null,
        status: 'wartend',
        turnusCount: turnusCount === '' ? null : parseInt(turnusCount, 10),
        turnusEvery: turnusEvery === '' ? null : parseInt(turnusEvery, 10),
        turnusUnit: (turnusCount && turnusEvery) ? turnusUnit : null,
        templateId: appliedTemplateIdRef.current || null,
        subprojects: hasSubprojects ? subprojects.map(sp => ({ name: sp.name })) : []
      };
      const res = await apiService.createProject(payload);
      return res;
    } finally {
      setLoading(false);
    }
  };

  const submitTimeblocksForProject = async (projectId) => {
    const blocks = timeblocks
      .filter(tb => (tb.titleSuffix || '').trim().length > 0 && (tb.assignedTo || '').trim().length > 0)
      .map(tb => ({
        title: buildTitleWithPrefix(name, tb.titleSuffix),
        description: tb.description || '',
        assignedTo: tb.assignedTo,
        plannedHours: (tb.plannedHours !== '' && tb.plannedHours != null) ? parseFloat(tb.plannedHours) : null,
        calenderMarker: tb.calenderMarker || null,
        checklist: Array.isArray(tb.checklist) && tb.checklist.length > 0 ? tb.checklist.map((c, i) => ({ content: c.content, sortOrder: i })) : undefined
      }));
    if (blocks.length === 0) return { success: true, count: 0 };
    return await apiService.createProjectTimeblocks(projectId, blocks);
  };

  const submitTimeblocksForSubprojects = async (createdSubprojects, indexMap = null) => {
    // createdSubprojects: array of { id, name }
    try {
      if (!Array.isArray(createdSubprojects) || createdSubprojects.length === 0) {
        return { success: true, count: 0 };
      }
      let total = 0;
      for (let i = 0; i < createdSubprojects.length; i++) {
        const sp = createdSubprojects[i];
        if (!sp || !sp.id) continue;
        // Map created subproject back to its original index within the wizard state
        const srcIdx = Array.isArray(indexMap) && indexMap[i] !== undefined ? indexMap[i] : i;
        // Prefer blocks captured via the side panel; fallback to step-3 structure if present (using srcIdx)
        const list = (subprojectTimeblocks[srcIdx] && Array.isArray(subprojectTimeblocks[srcIdx])
          ? subprojectTimeblocks[srcIdx]
          : (timeblocks[srcIdx]?.list || [])
        ) || [];
        const blocks = list
          .filter(tb => (tb.titleSuffix || '').trim().length > 0 && (tb.assignedTo || '').trim().length > 0)
          .map(tb => ({
            title: buildTitleWithPrefix(
              `${(initialMode === 'subproject' ? (initialData?.project?.name || name) : name || '').trim()} - ${(sp?.name || subprojects[srcIdx]?.name || '').trim()}`,
              tb.titleSuffix
            ),
            description: tb.description || '',
            assignedTo: tb.assignedTo,
            plannedHours: (tb.plannedHours !== '' && tb.plannedHours != null) ? parseFloat(tb.plannedHours) : null,
            calenderMarker: tb.calenderMarker || null,
            checklist: Array.isArray(tb.checklist) && tb.checklist.length > 0 ? tb.checklist.map((c, i) => ({ content: c.content, sortOrder: i })) : undefined
          }));
        if (blocks.length > 0) {
          const res = await apiService.createSubprojectTimeblocks(sp.id, blocks);
          total += (res?.count || 0);
        }
      }
      return { success: true, count: total };
    } catch (e) {
      console.error('Error submitting timeblocks for subprojects', e);
      return { success: false, error: e?.message };
    }
  };

  const handleFinishNoSubprojects = async () => {
    if (loading || isSubmitting) return;
    if (!isProjectBlocksAssigneesValid) {
      pushToast({ type: 'error', message: t('projectWizard.validation.selectAssigneeForAllBlocks', 'Bitte wählen Sie für alle definierten Zeitblöcke ein "Zugewiesen an" aus.') });
      return;
    }
    setIsSubmitting(true);
    try {
      if (initialMode === 'project' && initialData?.id) {
        // update existing project basics
        await apiService.updateProject(initialData.id, {
          name,
          description,
          responsibleEmail,
          customerId: customerId || null,
          turnusCount: turnusCount === '' ? null : parseInt(turnusCount, 10),
          turnusEvery: turnusEvery === '' ? null : parseInt(turnusEvery, 10),
          turnusUnit: (turnusCount && turnusEvery) ? turnusUnit : null
        });
        // diff existing vs current for updates/deletes/adds
        const current = timeblocks;
        const initialMap = new Map((initialBlocks || []).map(b => [b.id, b]));
        const currentWithId = current.filter(b => b.id);
        const currentIds = new Set(currentWithId.map(b => b.id));
        const updates = currentWithId.filter(b => {
          const orig = initialMap.get(b.id);
          if (!orig) return false;
          return (orig.titleSuffix || '') !== (b.titleSuffix || '') || (orig.description || '') !== (b.description || '') || (orig.assignedTo || '') !== (b.assignedTo || '') || String(orig.plannedHours || '') !== String(b.plannedHours || '') || (orig.calenderMarker || '') !== (b.calenderMarker || '');
        });
        const deletions = (initialBlocks || []).filter(b => !currentIds.has(b.id));
        const additions = current.filter(b => !b.id && (b.titleSuffix || '').trim() && (b.assignedTo || '').trim());

        // apply updates
        for (const u of updates) {
          await apiService.updateTicket(u.id, { title: buildTitleWithPrefix(name, u.titleSuffix), description: u.description || '', assignedTo: u.assignedTo || null, plannedHours: (u.plannedHours !== '' && u.plannedHours != null) ? parseFloat(u.plannedHours) : null, calenderMarker: u.calenderMarker || null });
        }
        // apply deletions
        for (const d of deletions) {
          await apiService.deleteTicket(d.id);
        }
        // apply additions
        if (additions.length > 0) {
          const blocks = additions.map(tb => ({ title: buildTitleWithPrefix(name, tb.titleSuffix), description: tb.description || '', assignedTo: tb.assignedTo, plannedHours: (tb.plannedHours !== '' && tb.plannedHours != null) ? parseFloat(tb.plannedHours) : null, calenderMarker: tb.calenderMarker || null, checklist: Array.isArray(tb.checklist) && tb.checklist.length > 0 ? tb.checklist.map((c, i) => ({ content: c.content, sortOrder: i })) : undefined }));
          await apiService.createProjectTimeblocks(initialData.id, blocks);
        }
        try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'project_updated_with_timeblocks' } })); } catch (_) {}
        if (onCreated) await onCreated({ ...initialData, name, description, responsibleEmail, customerId: customerId || null });
        return;
      }
      const res = await createProject();
      if (!res?.success) return;
      await submitTimeblocksForProject(res.project.id);
      // Globale Aktualisierungstrigger
      try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'project_created_with_timeblocks' } })); } catch (_) {}
      // Übergib das neue Projekt an Caller, damit dieser sofort selektieren/refreshen kann
      if (onCreated) await onCreated(res.project);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinishWithSubprojects = async () => {
    if (loading || isSubmitting) return;
    if (!isSubprojectBlocksAssigneesValid) {
      pushToast({ type: 'error', message: t('projectWizard.validation.selectAssigneeForAllBlocks', 'Bitte wählen Sie für alle definierten Zeitblöcke ein "Zugewiesen an" aus.') });
      return;
    }
    setIsSubmitting(true);
    try {
      // Editing existing project with subprojects: update project and subprojects
      if (initialMode === 'project' && initialData?.id && hasSubprojects) {
        try {
          await apiService.updateProject(initialData.id, {
            name,
            description,
            responsibleEmail,
            customerId: customerId || null,
            turnusCount: turnusCount === '' ? null : parseInt(turnusCount, 10),
            turnusEvery: turnusEvery === '' ? null : parseInt(turnusEvery, 10),
            turnusUnit: (turnusCount && turnusEvery) ? turnusUnit : null
          });
          // iterate through subprojects to update existing ones and add new ones
          const toAdd = [];
          const toAddIndexMap = [];
          for (let i = 0; i < subprojects.length; i++) {
            const sp = subprojects[i] || {};
            const details = subprojectDetails[i] || {};
            if (sp.id) {
              await apiService.updateSubproject(sp.id, { name: sp.name || '', description: details.description || '', responsibleEmail: details.responsibleEmail || '' });
            } else if ((sp.name || '').trim().length > 0) {
              toAdd.push({ name: sp.name || '', description: details.description || '', responsibleEmail: details.responsibleEmail || '' });
              toAddIndexMap.push(i);
            }
          }
          if (toAdd.length > 0) {
            const addRes = await apiService.addSubprojectsToProject(initialData.id, toAdd);
            const createdSubs = addRes?.subprojects || [];
            if (createdSubs.length > 0) {
              await submitTimeblocksForSubprojects(createdSubs, toAddIndexMap);
            }
          }
          try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'project_updated_with_timeblocks' } })); } catch (_) {}
          // Übergib geändertes Projekt an Caller zur sofortigen Aktualisierung/Selektion
          if (onCreated) await onCreated({ ...initialData, name });
          return;
        } catch (err) {
          console.error('Error finishing project with subprojects:', err);
          pushToast({ type: 'error', message: t('projectWizard.saveSubprojectsError', 'Fehler beim Speichern der Teilvorgänge oder Zeitblöcke. Details in der Konsole.') });
          return;
        }
      }
      if (initialMode === 'subproject' && initialData?.id) {
        // update subproject basics
        const details = subprojectDetails[0] || {};
        await apiService.updateSubproject(initialData.id, { name, description: details.description || '', responsibleEmail: details.responsibleEmail || '' });
        // diff for this subproject
        const current = (timeblocks[0]?.list || []);
        const initialMap = new Map((initialBlocks || []).map(b => [b.id, b]));
        const currentWithId = current.filter(b => b.id);
        const currentIds = new Set(currentWithId.map(b => b.id));
        const updates = currentWithId.filter(b => {
          const orig = initialMap.get(b.id);
          if (!orig) return false;
          return (orig.titleSuffix || '') !== (b.titleSuffix || '') || (orig.description || '') !== (b.description || '') || (orig.assignedTo || '') !== (b.assignedTo || '') || String(orig.plannedHours || '') !== String(b.plannedHours || '') || (orig.calenderMarker || '') !== (b.calenderMarker || '');
        });
        const deletions = (initialBlocks || []).filter(b => !currentIds.has(b.id));
        const additions = current.filter(b => !b.id && (b.titleSuffix || '').trim() && (b.assignedTo || '').trim());

        for (const u of updates) {
          await apiService.updateTicket(u.id, { title: buildTitleWithPrefix(name, u.titleSuffix), description: u.description || '', assignedTo: u.assignedTo || null, plannedHours: (u.plannedHours !== '' && u.plannedHours != null) ? parseFloat(u.plannedHours) : null, calenderMarker: u.calenderMarker || null });
        }
        for (const d of deletions) {
          await apiService.deleteTicket(d.id);
        }
        if (additions.length > 0) {
          const blocks = additions.map(tb => ({ title: buildTitleWithPrefix(name, tb.titleSuffix), description: tb.description || '', assignedTo: tb.assignedTo, plannedHours: (tb.plannedHours !== '' && tb.plannedHours != null) ? parseFloat(tb.plannedHours) : null, calenderMarker: tb.calenderMarker || null, checklist: Array.isArray(tb.checklist) && tb.checklist.length > 0 ? tb.checklist.map((c, i) => ({ content: c.content, sortOrder: i })) : undefined }));
          await apiService.createSubprojectTimeblocks(initialData.id, blocks);
        }
        try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'subproject_updated_with_timeblocks' } })); } catch (_) {}
        // Übergib geändertes Unterprojekt/Projekt an Caller
        if (onCreated) await onCreated({ ...initialData, name });
        return;
      }
      const res = await createProject();
      if (!res?.success) return;
      if (Array.isArray(res.subprojects) && res.subprojects.length > 0) {
        await submitTimeblocksForSubprojects(res.subprojects);
      }
      try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'project_created_with_subprojects_and_timeblocks' } })); } catch (_) {}
      // Sende das erstellte Projekt zurück, damit Caller direkt selektieren kann
      if (onCreated) await onCreated(res.project);
    } finally {
      setIsSubmitting(false);
    }
  };

  const StepHeader = ({ title }) => (
    <div className="modal-header">
      <div className="modal-title" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        <Ticket size={24} />
        <h3 style={{ marginLeft: 8 }}>{title}</h3>
      </div>
      <button onClick={onClose} className="close-btn">×</button>
    </div>
  );

  const applyTemplate = (tpl) => {
    try {
      const template = tpl || {};
      appliedTemplateIdRef.current = template.id || null;
      setName(template.name || '');
      setDescription(template.description || '');
      setResponsibleEmail(template.responsibleEmail || '');
      setTurnusCount(template.turnusCount != null ? String(template.turnusCount) : '');
      setTurnusEvery(template.turnusEvery != null ? String(template.turnusEvery) : '');
      setTurnusUnit(template.turnusUnit || 'days');
      const withSubs = !!template.hasSubprojects && Array.isArray(template.subprojects) && template.subprojects.length > 0;
      setHasSubprojects(withSubs);
      const tBlocks = Array.isArray(template.timeblocks) ? template.timeblocks : [];
      if (withSubs) {
        const subs = template.subprojects.map(sp => ({ name: sp.name || '' }));
        setSubprojects(subs.length > 0 ? subs : [{ name: '' }]);
        const details = template.subprojects.map(sp => ({ description: sp.description || '', responsibleEmail: sp.responsibleEmail || '' }));
        setSubprojectDetails(details);
        const idToIndex = new Map(template.subprojects.map((sp, idx) => [sp.id, idx]));
        const nextLists = new Array(template.subprojects.length).fill(null).map(() => []);
        for (const tb of tBlocks) {
          const spId = tb.templateSubprojectId;
          if (spId && idToIndex.has(spId)) {
            const idx = idToIndex.get(spId);
            nextLists[idx].push({ titleSuffix: tb.titleSuffix || '', description: tb.description || '', assignedTo: tb.assignedTo || '', plannedHours: tb.plannedHours || '', calenderMarker: tb.calenderMarker || '', checklist: Array.isArray(tb.checklist) ? tb.checklist : [] });
          }
        }
        const nextTimeblocks = nextLists.map(list => ({ list: (list.length > 0 ? list : [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '', calenderMarker: '', checklist: [] }]) }));
        setTimeblocks(nextTimeblocks);
        setSubprojectTimeblocks(nextLists);
      } else {
        setHasSubprojects(false);
        setSubprojects([{ name: '' }]);
        setSubprojectDetails([]);
        const projectBlocks = tBlocks.filter(tb => !tb.templateSubprojectId).map(tb => ({ titleSuffix: tb.titleSuffix || '', description: tb.description || '', assignedTo: tb.assignedTo || '', plannedHours: tb.plannedHours || '', calenderMarker: tb.calenderMarker || '', checklist: Array.isArray(tb.checklist) ? tb.checklist : [] }));
        setTimeblocks(projectBlocks.length > 0 ? projectBlocks : [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '', calenderMarker: '', checklist: [] }]);
        setSubprojectTimeblocks([]);
        setStep(1);
      }
    } catch (e) {
      try { console.error('Apply template error:', e); } catch (_) {}
    }
  };

  const handleAddToTemplates = async () => {
    try {
      const payload = {
        name,
        description,
        responsibleEmail: responsibleEmail || null,
        hasSubprojects: !!hasSubprojects,
        turnusCount: turnusCount === '' ? null : parseInt(turnusCount, 10),
        turnusEvery: turnusEvery === '' ? null : parseInt(turnusEvery, 10),
        turnusUnit: (turnusCount && turnusEvery) ? turnusUnit : null,
        subprojects: hasSubprojects ? (subprojects || []).map((sp, idx) => ({
          name: sp?.name || '',
          description: (subprojectDetails?.[idx]?.description) || '',
          responsibleEmail: (subprojectDetails?.[idx]?.responsibleEmail) || ''
        })) : [],
        timeblocks: []
      };
      if (hasSubprojects) {
        (subprojects || []).forEach((_, spIdx) => {
          const list = (Array.isArray(subprojectTimeblocks?.[spIdx])
            ? subprojectTimeblocks[spIdx]
            : (timeblocks?.[spIdx]?.list || [])
          );
          for (const tb of list) {
            if ((tb.titleSuffix || '').trim().length === 0) continue;
            payload.timeblocks.push({
              templateSubprojectIndex: spIdx,
              titleSuffix: tb.titleSuffix || '',
              description: tb.description || '',
              assignedTo: tb.assignedTo || '',
              plannedHours: (tb.plannedHours !== '' && tb.plannedHours != null) ? parseFloat(tb.plannedHours) : null,
              calenderMarker: tb.calenderMarker || null,
              checklist: Array.isArray(tb.checklist) && tb.checklist.length > 0 ? tb.checklist : null
            });
          }
        });
      } else {
        (timeblocks || []).forEach(tb => {
          if ((tb.titleSuffix || '').trim().length === 0) return;
          payload.timeblocks.push({
            templateSubprojectIndex: null,
            titleSuffix: tb.titleSuffix || '',
            description: tb.description || '',
            assignedTo: tb.assignedTo || '',
            plannedHours: (tb.plannedHours !== '' && tb.plannedHours != null) ? parseFloat(tb.plannedHours) : null,
            calenderMarker: tb.calenderMarker || null,
            checklist: Array.isArray(tb.checklist) && tb.checklist.length > 0 ? tb.checklist : null
          });
        });
      }
      // Statt confirm: Frage-Modal anzeigen, wenn bestehende Vorlage angewendet ist
      if (appliedTemplateIdRef.current) {
        setTemplateChoiceModal({ visible: true, payload });
        return;
      }

      const res = await apiService.createTemplate(payload);
      if (res?.success) {
        try { window.dispatchEvent(new CustomEvent('templatesUpdated', { detail: { template: res.template || null } })); } catch (_) {}
        pushToast({ message: t('projectWizard.templates.created', 'Vorlage wurde erstellt'), timeoutMs: 3000 });
        if (res.template && res.template.id) appliedTemplateIdRef.current = res.template.id;
      }
    } catch (e) {
      try { console.error('Create template error:', e); } catch (_) {}
      pushToast({ message: t('projectWizard.templates.createError', 'Fehler beim Speichern der Vorlage. Details in der Konsole.'), timeoutMs: 5000 });
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-flex-wrap" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 16 }}>
        {showTemplatesPanel && (
          <ProjectTemplatesPanel onApply={applyTemplate} />
        )}
        <div className={`modal-content create-project-modal ${(showSubTimeblocksPanel && step === 2) || showTemplatesPanel ? 'narrow' : ''}`}>
        {step === 1 && (
          <>
            <StepHeader title={(initialMode === 'project' && initialData?.id)
              ? t('projectWizard.step1.editTitle', 'Projekt überarbeiten')
              : t('projectWizard.step1.title', 'Neues Projekt erstellen')
            } />
            <form onSubmit={(e) => { e.preventDefault(); if (!readOnly) { setShowTemplatesPanel(false); setStep(hasSubprojects ? 2 : 3); } }} className="create-ticket-form">
              <div className="form-body">
                {/* #103: Kunde (halbbreit) über dem Projektnamen, wie in den Ticket-Dialogen */}
                <TicketCustomerField
                  value={customerId}
                  onChange={setCustomerId}
                  inputId="project-customer"
                  className="customer-field-half"
                  disabled={readOnly}
                />
                <div className="form-group">
                  <label>{t('projectWizard.labels.projectName', 'Projektname')} <span className="required">*</span></label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="form-input" required placeholder={t('projectWizard.placeholders.projectName', 'Projektname')} disabled={readOnly} />
                </div>
                <div className="form-group">
                  <label>{t('projectWizard.labels.projectDescription', 'Projektbeschreibung')}</label>
                  <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="3" className="form-textarea" placeholder={t('projectWizard.placeholders.description', 'Beschreibung')} disabled={readOnly} />
                </div>
                <div className="form-group">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontWeight: 'normal', fontSize: '14px' }}>{t('projectWizard.labels.projectResponsible', 'Projektverantwortlicher')}</label>
                    <label style={{ fontWeight: 'normal', fontSize: '14px' }}>
                      {t('projectWizard.turnus.heading', 'Turnus')}
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <select value={responsibleEmail} onChange={(e) => setResponsibleEmail(e.target.value)} className="form-select" disabled={readOnly} style={{ maxWidth: 160 }}>
                      <option value="">{t('projectWizard.options.none', 'Keiner')}</option>
                      {users.map(u => (
                        <option key={u.email} value={u.email}>{u.displayName || u.email}</option>
                      ))}
                    </select>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', flex: '0 0 auto' }}>
                      <input
                        type="number"
                        className="form-input"
                        placeholder={t('projectWizard.turnus.count', 'x mal')}
                        value={turnusCount}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') { setTurnusCount(''); return; }
                          const n = Math.max(1, Math.min(999, parseInt(v, 10) || 1));
                          setTurnusCount(String(n));
                        }}
                        min={1}
                        max={999}
                        disabled={turnusDisabled}
                        style={{ maxWidth: 100, width: 100, textAlign: 'right' }}
                      />
                      <input
                        type="number"
                        className="form-input"
                        placeholder={t('projectWizard.turnus.every', 'alle x')}
                        value={turnusEvery}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') { setTurnusEvery(''); return; }
                          const n = Math.max(1, Math.min(999, parseInt(v, 10) || 1));
                          setTurnusEvery(String(n));
                        }}
                        min={1}
                        max={999}
                        disabled={turnusDisabled}
                        style={{ maxWidth: 100, width: 100, textAlign: 'right' }}
                      />
                      <select
                        className="form-select"
                        value={turnusUnit}
                        onChange={(e) => setTurnusUnit(e.target.value)}
                        disabled={turnusDisabled}
                        style={{ textAlign: 'right' }}
                      >
                        <option value="days">{t('projectWizard.turnus.days', 'Tage')}</option>
                        <option value="weeks">{t('projectWizard.turnus.weeks', 'Wochen')}</option>
                        <option value="months">{t('projectWizard.turnus.months', 'Monate')}</option>
                        <option value="years">{t('projectWizard.turnus.years', 'Jahre')}</option>
                      </select>
                    </div>
                  </div>
                  {isEditingProject && (
                    <div style={{ marginTop: 4, color: 'var(--color-text-muted)', fontSize: 12 }}>
                      {t('projectWizard.info.turnusLocked', 'Turnus kann in "Projekt überarbeiten" nicht geändert werden.')}
                    </div>
                  )}
                  {!isEditingProject && (
                    <div style={{ marginTop: 4, color: 'var(--color-text-muted)', fontSize: 12 }}>
                      {t('projectWizard.turnus.hint', 'Angabe optional: z. B. „1 mal alle 1 Monate".')}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>{t('projectWizard.labels.subprojects', 'Teilvorgänge')}</label>
                  <div
                    style={{ display: 'flex', gap: 12, alignItems: 'center', opacity: subprojectsToggleDisabled ? 0.6 : 1, cursor: subprojectsToggleDisabled ? 'not-allowed' : 'default' }}
                    title={subprojectsToggleDisabled ? t('projectWizard.info.subprojectsToggleLocked', 'Der Teilvorgangs-Umschalter ist bei bestehenden Projekten gesperrt.') : undefined}
                  >
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="radio" name="subprojects" checked={!hasSubprojects} onChange={() => setHasSubprojects(false)} disabled={subprojectsToggleDisabled} />
                      {t('projectWizard.options.no', 'Ohne')}
                    </label>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="radio" name="subprojects" checked={hasSubprojects} onChange={() => setHasSubprojects(true)} disabled={subprojectsToggleDisabled} />
                      {t('projectWizard.options.with', 'Mit')}
                    </label>
                  </div>
                  {isEditingProject && (
                    <div style={{ marginTop: 6, color: 'var(--color-text-muted)', fontSize: 12 }}>
                      {t('projectWizard.info.subprojectsToggleLocked', 'Der Unterprojekt-Umschalter ist bei bestehenden Projekten gesperrt.')}
                    </div>
                  )}
                </div>
                {/* Bei "Mit Teilvorgängen" kommen die Detail-Definitionen (inkl. Namen) erst im Folge-Modal (Schritt 2) */}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={onClose}>{readOnly ? t('common.close', 'Schließen') : t('common.cancel', 'Abbrechen')}</button>
                {!readOnly && (
                  <button type="submit" className="btn btn-primary" disabled={!canContinueStep1}>{t('projectWizard.buttons.next', 'Weiter')}</button>
                )}
              </div>
            </form>
          </>
        )}

        {step === 2 && hasSubprojects && (
          <>
            <StepHeader title={t('projectWizard.step2.title', 'Teilvorgänge definieren')} />
            <div className="form-body">
              <div className="card" style={{ padding: 12 }}>
                {(subprojects.length === 0 ? [{ name: '' }] : subprojects).map((sp, idx) => (
                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, marginBottom: 12, background: 'linear-gradient(135deg, var(--color-bg) 0%, var(--color-border-light) 100%)', borderRadius: 8, border: '1px solid var(--color-border-light)', borderLeft: '3px solid var(--color-primary-legacy)', boxShadow: '0 1px 4px var(--color-shadow-05)' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('projectWizard.subproject.title', 'Teilvorgang Titel')}</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder={t('projectWizard.subproject.namePlaceholder', 'Teilvorgangsname')}
                        value={subprojects[idx]?.name || ''}
                        onChange={(e) => updateSubprojectName(idx, e.target.value)}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('projectWizard.subproject.description', 'Teilvorgang Beschreibung')}</label>
                      <textarea
                        className="form-textarea"
                        rows="2"
                        placeholder={t('projectWizard.placeholders.description', 'Beschreibung')}
                        value={subprojectDetails[idx]?.description || ''}
                        onChange={(e) => {
                          const next = [...subprojectDetails];
                          next[idx] = { ...(next[idx] || {}), description: e.target.value };
                          setSubprojectDetails(next);
                        }}
                        disabled={readOnly}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <select
                        className="form-select"
                        value={subprojectDetails[idx]?.responsibleEmail || ''}
                        onChange={(e) => {
                          const next = [...subprojectDetails];
                          next[idx] = { ...(next[idx] || {}), responsibleEmail: e.target.value };
                          setSubprojectDetails(next);
                        }}
                        disabled={readOnly}
                        style={{ maxWidth: 280 }}
                        required
                      >
                        <option value="">{t('projectWizard.subproject.responsibleRequired', 'Verantwortlicher (erforderlich)')}</option>
                        {users.map(u => (
                          <option key={u.email} value={u.email}>{u.displayName || u.email}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        title={t('projectWizard.buttons.configureTimeblocks', 'Zeitblöcke konfigurieren')}
                        onClick={() => {
                          const subId = initialMode === 'subproject' ? (initialData?.id || null) : (initialData?.subprojects?.[idx]?.id || null);
                          setActiveSubproject({ id: subId, name: subprojects[idx]?.name || initialData?.name || '', index: idx });
                          setShowSubTimeblocksPanel(true);
                        }}
                      >
                        <CalendarIcon size={16} /> {t('projectWizard.buttons.configureTimeblocks', 'Zeitblöcke konfigurieren')}
                      </button>
                      {!readOnly && (
                        <button type="button" className="btn btn-link" style={{ color: 'var(--color-text-muted)' }} title={t('projectWizard.buttons.deleteSubproject', 'Teilvorgang löschen')} onClick={async () => {
                          if (!(await confirm({ message: t('projectWizard.confirm.deleteSubprojectAndTickets', 'Diesen Teilvorgang und alle dazugehörigen Tickets endgültig löschen?'), danger: true }))) return;
                          try {
                            if (subprojects[idx]?.id) {
                              await apiService.deleteSubproject(subprojects[idx].id);
                            }
                            removeSubprojectAt(idx);
                          } catch (e) {
                            console.error(e);
                            // #336: Teilerfolg antwortet mit 409 + failedTickets — bisher verschwand
                            // das im console.error und der Teilvorgang blieb kommentarlos stehen.
                            const daten = e?.response?.data;
                            pushToast({
                              type: 'error',
                              message: daten?.error === 'PARTIAL_DELETE'
                                ? t('projectWizard.errors.deleteSubprojectPartial', 'Teilvorgang nicht gelöscht: {count} Ticket(s) konnten nicht entfernt werden.').replace('{count}', String(daten.failedTickets?.length ?? 0))
                                : t('projectWizard.errors.deleteSubproject', 'Teilvorgang konnte nicht gelöscht werden.')
                            });
                          }
                        }}>{t('projectWizard.buttons.deleteSubproject', 'Teilvorgang löschen')}</button>
                      )}
                    </div>
                    {/* Divider entfernt, Umrandung mit Schatten hervorgehoben */}
                  </div>
                ))}
                {!readOnly && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => insertSubprojectAt(subprojects.length - 1)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Plus size={16} /> {t('projectWizard.buttons.addSubproject', 'Weiterer Teilvorgang')}
                  </button>
                )}
              </div>
            </div>
            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {(!readOnly && istManager) ? (
                isCreatingNew ? (
                  <button type="button" className="btn btn-secondary" onClick={handleAddToTemplates}>
                    {appliedTemplateIdRef.current
                      ? t('projectWizard.buttons.updateTemplate', 'Bibliotheks-Eintrag anpassen')
                      : t('projectWizard.buttons.addToTemplates', 'Zur Bibliothek hinzufügen')}
                  </button>
                ) : (
                  appliedTemplateIdRef.current ? (
                    <button type="button" className="btn btn-secondary" onClick={handleAddToTemplates}>
                      {t('projectWizard.buttons.updateTemplate', 'Bibliotheks-Eintrag anpassen')}
                    </button>
                  ) : <div />
                )
              ) : <div />}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>{readOnly ? t('common.close', 'Schließen') : t('projectWizard.buttons.back', 'Zurück')}</button>
                {!readOnly && (
                  <button type="button" className="btn btn-primary" onClick={handleFinishWithSubprojects} disabled={isSubmitting}>
                    {isSubmitting
                      ? t('common.saving', 'Speichere...')
                      : (initialMode === 'project' || initialMode === 'subproject')
                        ? t('projectWizard.buttons.saveChanges', 'Änderungen speichern')
                        : t('projectWizard.buttons.create', 'Anlegen')}
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <StepHeader title={t('projectWizard.step3.title', 'Zeitblöcke definieren')} />
            <div className="form-body">
              {!hasSubprojects && (
                <div className="card" style={{ padding: 12 }}>
                  {timeblocks.map((tb, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, alignItems: 'start', marginBottom: 12, background: 'linear-gradient(135deg, var(--color-bg) 0%, var(--color-border-light) 100%)', borderRadius: 8, border: '1px solid var(--color-border-light)', borderLeft: '3px solid var(--color-primary-legacy)', boxShadow: '0 1px 4px var(--color-shadow-05)', padding: 8 }}>
                      <input type="text" className="form-input" placeholder={t('projectWizard.timeblock.namePlaceholder', 'ZEITBLOCKNAME')} value={tb.titleSuffix} onChange={(e) => updateTimeblock(idx, { titleSuffix: e.target.value })} disabled={readOnly} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CalenderMarkerHeaderSelect
                          value={tb.calenderMarker || ''}
                          onChange={(value) => updateTimeblock(idx, { calenderMarker: value })}
                        />
                        <input type="number" className="form-input" value={tb.plannedHours || ''} onChange={(e) => {
                          const v = e.target.value;
                          let nextVal = v;
                          if (v !== '') {
                            const num = Math.max(0, Math.min(12, parseFloat(v)));
                            nextVal = Number.isNaN(num) ? '' : String(num);
                          }
                          updateTimeblock(idx, { plannedHours: nextVal });
                        }} step={0.25} min={0} max={12} placeholder="Std." disabled={readOnly} style={{ maxWidth: 90 }} />
                      </div>
                      <select className="form-select" value={tb.assignedTo} onChange={(e) => updateTimeblock(idx, { assignedTo: e.target.value })} disabled={readOnly} required style={{ width: 300, minWidth: 300, maxWidth: 300 }}>
                        <option value="">{t('projectWizard.placeholders.assignedTo', 'Zugewiesen an...')}</option>
                        {users.map(u => (
                          <option key={u.email} value={u.email}>{u.displayName || u.email}</option>
                        ))}
                      </select>
                      {!readOnly && (
                        <button type="button" className="btn btn-secondary" title={t('projectWizard.timeblock.delete', 'Zeitblock löschen')} onClick={() => removeTimeblock(idx)} style={{ minWidth: 40 }}>–</button>
                      )}
                      {/* Zeile 2: Checklisten-Button + Beschreibung */}
                      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'stretch' }}>
                        <button
                          type="button"
                          onClick={() => updateTimeblock(idx, { showChecklist: !tb.showChecklist })}
                          title={tb.showChecklist ? 'Checkliste ausblenden' : 'Checkliste'} aria-label={tb.showChecklist ? 'Checkliste ausblenden' : 'Checkliste'}
                          className="btn"
                          style={{
                            background: tb.showChecklist ? 'var(--color-accent-2)' : 'transparent',
                            color: tb.showChecklist ? 'var(--color-text-inverse)' : 'var(--color-text-light)',
                            border: '1px solid',
                            borderColor: tb.showChecklist ? 'var(--color-accent-2)' : 'var(--color-border-neutral)',
                            borderRadius: 8,
                            padding: '6px 8px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <CheckSquare size={16} />
                        </button>
                        <textarea
                          className="form-textarea"
                          placeholder={t('projectWizard.placeholders.description', 'Beschreibung')}
                          value={tb.description}
                          onChange={(e) => updateTimeblock(idx, { description: e.target.value })}
                          disabled={readOnly}
                          rows={2}
                          style={{ flex: 1, resize: 'vertical', minHeight: 40 }}
                        />
                      </div>
                      {/* Zeile 3: Checkliste */}
                      {tb.showChecklist && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <TimeblockChecklist
                            items={tb.checklist || []}
                            onChange={(newChecklist) => updateTimeblock(idx, { checklist: newChecklist })}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  {!readOnly && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={addTimeblock} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Plus size={16} /> {t('projectWizard.buttons.addTimeblock', 'Zeitblock')}
                    </button>
                  )}
                  <div style={{ marginTop: 8, color: 'var(--color-text-light)', fontSize: 12 }}>
                    {t('projectWizard.hints.titleSavedAs', 'Titel wird als „{{project}} - ZEITBLOCKNAME" gespeichert.', { project: name })}
                  </div>
                </div>
              )}

              {hasSubprojects && (
                <div className="card" style={{ padding: 12 }}>
                  {subprojects.map((sp, spIdx) => (
                    <div key={spIdx} style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>{sp.name}</div>
                      {(timeblocks[spIdx]?.list || [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '', checklist: [] }]).map((tb, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, alignItems: 'start', marginBottom: 12, background: 'linear-gradient(135deg, var(--color-bg) 0%, var(--color-border-light) 100%)', borderRadius: 8, border: '1px solid var(--color-border-light)', borderLeft: '3px solid var(--color-primary-legacy)', boxShadow: '0 1px 4px var(--color-shadow-05)', padding: 8 }}>
                          <input type="text" className="form-input" placeholder={t('projectWizard.timeblock.namePlaceholder', 'ZEITBLOCKNAME')} value={tb.titleSuffix} onChange={(e) => {
                            const next = [...timeblocks];
                            const list = [...(next[spIdx]?.list || [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '' }])];
                            list[idx] = { ...list[idx], titleSuffix: e.target.value };
                            next[spIdx] = { list };
                            setTimeblocks(next);
                          }} disabled={readOnly} />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CalenderMarkerHeaderSelect
                              value={tb.calenderMarker || ''}
                              onChange={(value) => {
                                const next = [...timeblocks];
                                const list = [...(next[spIdx]?.list || [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '' }])];
                                list[idx] = { ...list[idx], calenderMarker: value };
                                next[spIdx] = { list };
                                setTimeblocks(next);
                              }}
                            />
                            <input type="number" className="form-input" value={tb.plannedHours || ''} onChange={(e) => {
                              const v = e.target.value;
                              let nextVal = v;
                              if (v !== '') {
                                const num = Math.max(0, Math.min(12, parseFloat(v)));
                                nextVal = Number.isNaN(num) ? '' : String(num);
                              }
                              const next = [...timeblocks];
                              const list = [...(next[spIdx]?.list || [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '' }])];
                              list[idx] = { ...list[idx], plannedHours: nextVal };
                              next[spIdx] = { list };
                              setTimeblocks(next);
                            }} step={0.25} min={0} max={12} placeholder="Std." disabled={readOnly} style={{ maxWidth: 90 }} />
                          </div>
                          <select className="form-select" value={tb.assignedTo} onChange={(e) => {
                            const next = [...timeblocks];
                            const list = [...(next[spIdx]?.list || [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '' }])];
                            list[idx] = { ...list[idx], assignedTo: e.target.value };
                            next[spIdx] = { list };
                            setTimeblocks(next);
                          }} disabled={readOnly} required style={{ width: 300, minWidth: 300, maxWidth: 300 }}>
                            <option value="">{t('projectWizard.placeholders.assignedTo', 'Zugewiesen an...')}</option>
                            {users.map(u => (
                              <option key={u.email} value={u.email}>{u.displayName || u.email}</option>
                            ))}
                          </select>
                          {!readOnly && (
                            <button type="button" className="btn btn-secondary" title={t('projectWizard.timeblock.delete', 'Zeitblock löschen')} onClick={async () => {
                              if (!(await confirm({ message: t('projectWizard.confirm.deleteTimeblock', 'Diesen Zeitblock löschen?'), danger: true }))) return;
                              const next = [...timeblocks];
                              const list = [...(next[spIdx]?.list || [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '' }])];
                              list.splice(idx, 1);
                              next[spIdx] = { list };
                              setTimeblocks(next);
                            }} style={{ minWidth: 40 }}>–</button>
                          )}
                          {/* Zeile 2: Checklisten-Button + Beschreibung */}
                          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'stretch' }}>
                            <button
                              type="button"
                              onClick={() => {
                                const next = [...timeblocks];
                                const list = [...(next[spIdx]?.list || [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '' }])];
                                list[idx] = { ...list[idx], showChecklist: !tb.showChecklist };
                                next[spIdx] = { list };
                                setTimeblocks(next);
                              }}
                              title={tb.showChecklist ? 'Checkliste ausblenden' : 'Checkliste'} aria-label={tb.showChecklist ? 'Checkliste ausblenden' : 'Checkliste'}
                              className="btn"
                              style={{
                                background: tb.showChecklist ? 'var(--color-accent-2)' : 'transparent',
                                color: tb.showChecklist ? 'var(--color-text-inverse)' : 'var(--color-text-light)',
                                border: '1px solid',
                                borderColor: tb.showChecklist ? 'var(--color-accent-2)' : 'var(--color-border-neutral)',
                                borderRadius: 8,
                                padding: '6px 8px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                            >
                              <CheckSquare size={16} />
                            </button>
                            <textarea
                              className="form-textarea"
                              placeholder={t('projectWizard.placeholders.description', 'Beschreibung')}
                              value={tb.description}
                              onChange={(e) => {
                                const next = [...timeblocks];
                                const list = [...(next[spIdx]?.list || [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '' }])];
                                list[idx] = { ...list[idx], description: e.target.value };
                                next[spIdx] = { list };
                                setTimeblocks(next);
                              }}
                              disabled={readOnly}
                              rows={2}
                              style={{ flex: 1, resize: 'vertical', minHeight: 40 }}
                            />
                          </div>
                          {/* Zeile 3: Checkliste */}
                          {tb.showChecklist && (
                            <div style={{ gridColumn: '1 / -1' }}>
                              <TimeblockChecklist
                                items={tb.checklist || []}
                                onChange={(newChecklist) => {
                                  const next = [...timeblocks];
                                  const list = [...(next[spIdx]?.list || [{ titleSuffix: '', description: '', assignedTo: '', plannedHours: '' }])];
                                  list[idx] = { ...list[idx], checklist: newChecklist };
                                  next[spIdx] = { list };
                                  setTimeblocks(next);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      {!readOnly && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                          const next = [...timeblocks];
                          const list = [...(next[spIdx]?.list || [])];
                          list.push({ titleSuffix: '', description: '', assignedTo: '', plannedHours: '', calenderMarker: '', checklist: [] });
                          next[spIdx] = { list };
                          setTimeblocks(next);
                        }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Plus size={16} /> {t('projectWizard.buttons.addTimeblockFor', 'Zeitblock für {{name}}', { name: sp.name })}
                        </button>
                      )}
                      <div className="wizard-divider" />
                    </div>
                  ))}
                  <div style={{ marginTop: 8, color: 'var(--color-text-light)', fontSize: 12 }}>
                    {t('projectWizard.hints.titleSavedAs', 'Titel wird als „{{project}} - ZEITBLOCKNAME" gespeichert.', { project: name })}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {(readOnly || !istManager) ? (
                <div />
              ) : (
                isCreatingNew ? (
                  <button type="button" className="btn btn-secondary" onClick={handleAddToTemplates}>
                    {appliedTemplateIdRef.current
                      ? t('projectWizard.buttons.updateTemplate', 'Bibliotheks-Eintrag anpassen')
                      : t('projectWizard.buttons.addToTemplates', 'Zur Bibliothek hinzufügen')}
                  </button>
                ) : (
                  appliedTemplateIdRef.current ? (
                    <button type="button" className="btn btn-secondary" onClick={handleAddToTemplates}>
                      {t('projectWizard.buttons.updateTemplate', 'Bibliotheks-Eintrag anpassen')}
                    </button>
                  ) : <div />
                )
              )}
              {readOnly ? (
                <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.close', 'Schließen')}</button>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setStep(hasSubprojects ? 2 : 1)}>{t('projectWizard.buttons.back', 'Zurück')}</button>
                  {!hasSubprojects ? (
                    <button type="button" className="btn btn-primary" onClick={handleFinishNoSubprojects} disabled={isSubmitting || loading || !isProjectBlocksAssigneesValid}>
                      {isSubmitting
                        ? t('common.saving', 'Speichere...')
                        : (initialMode === 'project' || initialMode === 'subproject')
                          ? t('projectWizard.buttons.saveChanges', 'Änderungen speichern')
                          : t('projectWizard.buttons.create', 'Anlegen')}
                    </button>
                  ) : (
                    <button type="button" className="btn btn-primary" onClick={handleFinishWithSubprojects} disabled={isSubmitting || loading || !isSubprojectBlocksAssigneesValid}>
                      {isSubmitting
                        ? t('common.saving', 'Speichere...')
                        : (initialMode === 'project' || initialMode === 'subproject')
                          ? t('projectWizard.buttons.saveChanges', 'Änderungen speichern')
                          : t('projectWizard.buttons.create', 'Anlegen')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
        </div>
        {showSubTimeblocksPanel && activeSubproject && (
          <SubprojectTimeblocksPanel
            subprojectId={activeSubproject.id}
            subprojectIndex={activeSubproject.index}
            subprojectName={activeSubproject.name}
            projectName={initialMode === 'subproject' ? (initialData?.project?.name || '') : name}
            users={users}
            getBlocks={(idx) => subprojectTimeblocks[idx] || []}
            setBlocks={(idx, list) => {
              const next = [...subprojectTimeblocks];
              next[idx] = list;
              setSubprojectTimeblocks(next);
            }}
            onClose={() => { setShowSubTimeblocksPanel(false); setActiveSubproject(null); }}
          />
        )}
        {templateChoiceModal.visible && templateChoiceModal.payload && (
          <Modal open onClose={() => !templateChoiceSaving && setTemplateChoiceModal({ visible: false, payload: null })} title={t('projectWizard.templates.choiceTitle', 'Bibliothekseintrag anpassen oder neu erstellen?')} maxWidth="560px" closeDisabled={templateChoiceSaving}>
                <p style={{ marginBottom: 12 }}>{t('projectWizard.templates.choiceText', 'Sie haben eine Vorlage angewendet. Wie möchten Sie fortfahren?')}</p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-secondary"
                    disabled={templateChoiceSaving}
                    onClick={async () => {
                      try {
                        setTemplateChoiceSaving(true);
                        const res = await apiService.updateTemplate(appliedTemplateIdRef.current, templateChoiceModal.payload);
                        if (res?.success) {
                          try { window.dispatchEvent(new CustomEvent('templatesUpdated', { detail: { template: res.template || null } })); } catch (_) {}
                          pushToast({ message: t('projectWizard.templates.updated', 'Vorlage wurde aktualisiert'), timeoutMs: 3000 });
                          if (res.template && res.template.id) appliedTemplateIdRef.current = res.template.id;
                        }
                      } catch (err) {
                        try { console.error('Update template error:', err); } catch (_) {}
                        pushToast({ message: t('projectWizard.templates.createError', 'Fehler beim Speichern der Vorlage. Details in der Konsole.'), timeoutMs: 5000 });
                      } finally {
                        setTemplateChoiceSaving(false);
                        setTemplateChoiceModal({ visible: false, payload: null });
                      }
                    }}
                  >
                    {templateChoiceSaving ? t('common.saving', 'Speichere...') : t('projectWizard.templates.updateExisting', 'Bestehenden Eintrag anpassen')}
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={templateChoiceSaving}
                    onClick={async () => {
                      try {
                        setTemplateChoiceSaving(true);
                        const res = await apiService.createTemplate(templateChoiceModal.payload);
                        if (res?.success) {
                          try { window.dispatchEvent(new CustomEvent('templatesUpdated', { detail: { template: res.template || null } })); } catch (_) {}
                          pushToast({ message: t('projectWizard.templates.created', 'Vorlage wurde erstellt'), timeoutMs: 3000 });
                          if (res.template && res.template.id) appliedTemplateIdRef.current = res.template.id;
                        }
                      } catch (err) {
                        try { console.error('Create template error:', err); } catch (_) {}
                        pushToast({ message: t('projectWizard.templates.createError', 'Fehler beim Speichern der Vorlage. Details in der Konsole.'), timeoutMs: 5000 });
                      } finally {
                        setTemplateChoiceSaving(false);
                        setTemplateChoiceModal({ visible: false, payload: null });
                      }
                    }}
                  >
                    {templateChoiceSaving ? t('common.saving', 'Speichere...') : t('projectWizard.templates.createNew', 'Neue Vorlage erstellen')}
                  </button>
                </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

// Linkes Panel für Vorlagenliste und Übernahme
const ProjectTemplatesPanel = ({ onApply }) => {
  const istManager = useIstManager();
  const { t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const latestReqRef = useRef(0);
  const [search, setSearch] = useState('');

  const filteredTemplates = useMemo(() => {
    try {
      const list = Array.isArray(templates) ? templates : [];
      const q = (search || '').toString().trim().toLowerCase();
      const filtered = !q ? list : list.filter((tpl) => {
        const fields = [];
        fields.push((tpl?.name || '').toString());
        fields.push((tpl?.description || '').toString());
        fields.push((tpl?.responsibleEmail || '').toString());
        if (Array.isArray(tpl?.subprojects)) {
          for (const sp of tpl.subprojects) {
            fields.push((sp?.name || '').toString());
            fields.push((sp?.description || '').toString());
            fields.push((sp?.responsibleEmail || '').toString());
          }
        }
        if (Array.isArray(tpl?.timeblocks)) {
          for (const tb of tpl.timeblocks) {
            fields.push((tb?.titleSuffix || '').toString());
            fields.push((tb?.description || '').toString());
            fields.push((tb?.assignedTo || '').toString());
          }
        }
        return fields.some((f) => f.toLowerCase().includes(q));
      });
      // Sortiere: ausgegraute (erfüllte) ans Ende
      return [...filtered].sort((a, b) => {
        const ad = !!a?._dimmed;
        const bd = !!b?._dimmed;
        if (ad === bd) return 0;
        return ad ? 1 : -1;
      });
    } catch (_) {
      return Array.isArray(templates) ? templates : [];
    }
  }, [templates, search]);

  const load = useCallback(async () => {
    const mySeq = ++latestReqRef.current;
    setLoading(true);
    try {
      const res = await apiService.getTemplates();
      if (mySeq !== latestReqRef.current) return;
      setTemplates(res.templates || []);
    } catch (e) {
      try { console.error('Error loading templates', e); } catch (_) {}
    } finally {
      if (mySeq === latestReqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onUpdate = (e) => {
      const tpl = e?.detail?.template || null;
      if (tpl && tpl.id) {
        setTemplates(prev => {
          const exists = (Array.isArray(prev) ? prev : []).some(p => String(p.id) === String(tpl.id));
          if (exists) return prev;
          return [tpl, ...(Array.isArray(prev) ? prev : [])];
        });
        return;
      }
      load();
    };
    const onTickets = () => { load(); };
    try { window.addEventListener('templatesUpdated', onUpdate); } catch (_) {}
    try { window.addEventListener('ticketsUpdated', onTickets); } catch (_) {}
    return () => {
      try { window.removeEventListener('templatesUpdated', onUpdate); } catch (_) {}
      try { window.removeEventListener('ticketsUpdated', onTickets); } catch (_) {}
    };
  }, [load]);

  const handleDelete = async (tpl) => {
    try {
      if (!tpl || !tpl.id) return;
      if (!(await confirm({ message: t('projectWizard.templates.confirmDelete', 'Diese Vorlage wirklich löschen?'), danger: true }))) return;
      await apiService.deleteTemplate(tpl.id);
      setTemplates((prev) => (Array.isArray(prev) ? prev.filter(p => String(p.id) !== String(tpl.id)) : prev));
      pushToast({
        message: t('projectWizard.templates.deleted', 'Vorlage gelöscht'),
        actionLabel: t('projectWizard.templates.undo', 'Rückgängig'),
        timeoutMs: 5000,
        onAction: async () => {
          try {
            const payload = { ...tpl };
            const res = await apiService.restoreTemplate(payload);
            const restored = res?.template;
            if (restored) {
              setTemplates((prev) => {
                const list = Array.isArray(prev) ? [...prev] : [];
                list.unshift(restored);
                return list;
              });
              pushToast({ message: t('projectWizard.templates.restored', 'Vorlage wiederhergestellt'), timeoutMs: 3000 });
            } else {
              pushToast({ message: t('projectWizard.templates.restoreError', 'Fehler beim Wiederherstellen der Vorlage.'), timeoutMs: 5000 });
            }
          } catch (e) {
            try { console.error('Restore template error:', e); } catch (_) {}
            pushToast({ message: t('projectWizard.templates.restoreError', 'Fehler beim Wiederherstellen der Vorlage.'), timeoutMs: 5000 });
          }
        }
      });
    } catch (e) {
      try { console.error('Delete template error:', e); } catch (_) {}
      
      // Check if error is due to active projects
      if (e?.response?.data?.error === 'TEMPLATE_HAS_ACTIVE_PROJECTS') {
        const count = e.response.data.activeProjectsCount || 0;
        pushToast({ 
          message: t('projectWizard.templates.hasActiveProjects', 'Diese Vorlage kann nicht gelöscht werden, da {{count}} aktive Turnus-Projekte damit verknüpft sind.', { count }), 
          timeoutMs: 7000 
        });
      } else {
        pushToast({ message: t('projectWizard.templates.deleteError', 'Fehler beim Löschen der Vorlage. Details in der Konsole.'), timeoutMs: 5000 });
      }
    }
  };

  return (
    <div className="modal-content create-ticket-modal" style={{ maxWidth: 420 }}>
      <div className="modal-header">
        <div className="modal-title" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Ticket size={24} />
          <h3 style={{ marginLeft: 8 }}>{t('projectWizard.templates.title', 'Bibliothek')}</h3>
        </div>
      </div>
      <div className="form-body">
        {loading ? (
          <div style={{ padding: 12 }}>{t('projectWizard.templates.loading', 'Lade Bibliothek...')}</div>
        ) : (
          <>
            <div style={{ marginBottom: 8 }}>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
                <input
                  type="text"
                  className="form-input"
                  placeholder={t('projectWizard.templates.searchPlaceholder', 'Bibliothek durchsuchen...')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 28 }}
                />
              </div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              {templates.length === 0 && (
                <div style={{ color: 'var(--color-text-muted)' }}>{t('projectWizard.templates.none', 'Keine Einträge in der Bibliothek')}</div>
              )}
              {templates.length > 0 && filteredTemplates.length === 0 && (
                <div style={{ color: 'var(--color-text-muted)' }}>{t('search.noResults', 'Keine Ergebnisse')}</div>
              )}
              {templates.length > 0 && filteredTemplates.length > 0 && (
                filteredTemplates.map((tpl) => {
                  if (!((tpl && tpl.id) != null)) return null;

                  const turnusSkipUntilDate = tpl.turnusSkipUntil ? new Date(tpl.turnusSkipUntil) : null;
                  const skipActiveByDate = !!(turnusSkipUntilDate && !Number.isNaN(turnusSkipUntilDate.getTime()) && turnusSkipUntilDate.getTime() >= Date.now());
                  const skipFlag = !!(tpl._turnusSkippedOnce || tpl.skipTurnusOnce || tpl.turnusSkippedOnce || skipActiveByDate);
                  const isDimmed = !!(tpl._dimmed || skipFlag);

                  return (
                  <div key={tpl.id} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 6, padding: 10, marginBottom: 10, background: 'linear-gradient(135deg, var(--color-bg) 0%, var(--color-border-light) 100%)', borderRadius: 8, border: '1px solid var(--color-border-light)', borderLeft: '3px solid var(--color-border-neutral-strong)', boxShadow: '0 1px 4px var(--color-shadow-05)', opacity: isDimmed ? 0.6 : 1 }}>
                    {tpl._dueThisPeriod ? (
                      <div style={{ position: 'absolute', top: 0, right: 0, width: 0, height: 0, borderTop: '18px solid var(--color-danger)', borderLeft: '18px solid transparent', borderTopRightRadius: 8 }} />
                    ) : null}
                    <div style={{ fontWeight: 600 }}>{tpl.name}</div>
                    {tpl._dueThisPeriod ? (
                      <div style={{ color: 'var(--color-danger)', fontSize: 13 }}>
                        {t('projectWizard.templates.dueThisPeriod', 'Noch für diesen Zeitraum einzuplanen')}
                      </div>
                    ) : (
                      isDimmed ? (
                        <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
                          {skipFlag
                            ? t('projectWizard.templates.turnusSkippedOnce', 'Aktueller Turnus übersprungen')
                            : t('projectWizard.templates.fulfilledThisPeriod', 'Bereits laut Turnus eingeplant')}
                        </div>
                      ) : (
                        tpl.description ? <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{tpl.description}</div> : null
                      )
                    )}
                    {(tpl.turnusCount && tpl.turnusEvery && tpl.turnusUnit) ? (
                      <div style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                        {tpl._lastPlannedAt
                          ? `${t('projects.lastPlannedAt', 'Zuletzt eingeplant')}: ${new Date(tpl._lastPlannedAt).toLocaleDateString()}`
                          : t('projects.neverPlanned', 'Noch nie eingeplant')}
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      {!istManager ? <div /> : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        title={t('projectWizard.templates.delete', 'Löschen')} aria-label={t('projectWizard.templates.delete', 'Löschen')}
                        onClick={() => handleDelete(tpl)}
                        style={{
                          padding: '2px',
                          fontSize: 12,
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--color-danger)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          minWidth: 20
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                      )}

                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {istManager && !!(tpl.turnusCount && tpl.turnusEvery && tpl.turnusUnit) && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '2px 6px', fontSize: 12 }}
                            onClick={async () => {
                              if (!(await confirm({ message: t('projectWizard.templates.skipTurnusConfirm', 'Soll der Turnus einmalig übersprungen werden?') }))) return;
                              try {
                                const res = await apiService.skipTemplateTurnusOnce(tpl.id);
                                if (res?.success) {
                                  // Sofort neu laden, damit Badge/Due-Status aktualisiert wird
                                  await load();
                                  try { window.dispatchEvent(new CustomEvent('templatesUpdated', { detail: { template: res.template || null } })); } catch (_) {}
                                  pushToast({ message: t('projectWizard.templates.skippedOnce', 'Turnus einmalig übersprungen'), timeoutMs: 3000 });
                                }
                              } catch (e) {
                                console.error('Skip turnus once error:', e);
                                pushToast({ message: t('projectWizard.templates.skipTurnusError', 'Fehler beim Überspringen des Turnus.'), timeoutMs: 5000 });
                              }
                            }}
                          >
                            {t('projectWizard.templates.skipTurnus', 'Turnus überspringen')}
                          </button>
                        )}

                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{ padding: '2px 6px', fontSize: 12 }}
                          onClick={() => onApply && onApply(tpl)}
                        >
                          {t('projectWizard.templates.apply', 'Anwenden')}
                        </button>
                      </div>
                    </div>
                  </div>
                  )
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Side panel for subproject timeblocks configuration
const SubprojectTimeblocksPanel = ({ subprojectId, subprojectIndex, subprojectName, projectName, users, getBlocks, setBlocks, onClose }) => {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [blocksState, setBlocksState] = useState([]);
  const [initialBlocks, setInitialBlocks] = useState([]);

  useEffect(() => {
    (async () => {
      if (subprojectId) {
        try {
          const res = await apiService.getTickets({ subprojectId });
          const list = (res.tickets || []).map(t => ({
            id: t.id,
            titleSuffix: stripKnownPrefixes(t.title, (projectName || ''), (subprojectName || '')),
            description: t.description || '',
            assignedTo: t.assignedTo || '',
            plannedHours: (t.plannedHours != null ? String(t.plannedHours) : ''),
            calenderMarker: t.calenderMarker || ''
          }));
          setBlocksState(list);
          setInitialBlocks(list);
        } finally {
          setLoading(false);
        }
      } else {
        // creation flow: use staged blocks from state
        const list = getBlocks(subprojectIndex) || [];
        setBlocksState(list);
        setInitialBlocks(list);
        setLoading(false);
      }
    })();
  }, [subprojectId, subprojectIndex]);

  const insertAt = (idx) => {
    const next = [...blocksState];
    next.splice(idx + 1, 0, { titleSuffix: '', description: '', assignedTo: '', plannedHours: '', calenderMarker: '', checklist: [] });
    setBlocksState(next);
  };
  const updateAt = (idx, patch) => {
    const next = [...blocksState];
    next[idx] = { ...next[idx], ...patch };
    setBlocksState(next);
  };
  const removeAt = async (idx) => {
    if (!(await confirm({ message: t('projectWizard.confirm.deleteTimeblock', 'Diesen Zeitblock löschen?'), danger: true }))) return;
    setBlocksState(prev => prev.filter((_, i) => i !== idx));
  };

  const isBlocksAssigneesValid = useMemo(() => {
    return (blocksState || []).every(tb => {
      const hasContent = !!tb.id || ((tb.titleSuffix || '').trim().length > 0) || ((tb.description || '').trim().length > 0);
      if (!hasContent) return true;
      return (tb.assignedTo || '').trim().length > 0;
    });
  }, [blocksState]);

  const handleSave = async () => {
    if (!isBlocksAssigneesValid) {
      pushToast({ type: 'error', message: t('projectWizard.validation.selectAssigneeForAllBlocks', 'Bitte wählen Sie für alle definierten Zeitblöcke ein "Zugewiesen an" aus.') });
      return;
    }
    if (subprojectId) {
      const initialMap = new Map((initialBlocks || []).map(b => [b.id, b]));
      const currentWithId = blocksState.filter(b => b.id);
      const currentIds = new Set(currentWithId.map(b => b.id));
      const updates = currentWithId.filter(b => {
        const orig = initialMap.get(b.id);
        if (!orig) return false;
        return (orig.titleSuffix || '') !== (b.titleSuffix || '') || (orig.description || '') !== (b.description || '') || (orig.assignedTo || '') !== (b.assignedTo || '') || String(orig.plannedHours || '') !== String(b.plannedHours || '') || (orig.calenderMarker || '') !== (b.calenderMarker || '');
      });
      const deletions = (initialBlocks || []).filter(b => !currentIds.has(b.id));
      const additions = blocksState.filter(b => !b.id && (b.titleSuffix || '').trim() && (b.assignedTo || '').trim());

      for (const u of updates) {
        const prefix = projectName ? `${projectName} - ${subprojectName}` : subprojectName;
        await apiService.updateTicket(u.id, { title: `${prefix} - ${u.titleSuffix.trim()}`, description: u.description || '', assignedTo: u.assignedTo || null, plannedHours: (u.plannedHours !== '' && u.plannedHours != null) ? parseFloat(u.plannedHours) : null });
      }
      for (const d of deletions) {
        await apiService.deleteTicket(d.id);
      }
      if (additions.length > 0) {
        const prefix = projectName ? `${projectName} - ${subprojectName}` : subprojectName;
        const payload = additions.map(tb => ({ title: `${prefix} - ${tb.titleSuffix.trim()}`, description: tb.description || '', assignedTo: tb.assignedTo, plannedHours: (tb.plannedHours !== '' && tb.plannedHours != null) ? parseFloat(tb.plannedHours) : null, checklist: Array.isArray(tb.checklist) ? tb.checklist : undefined }));
        await apiService.createSubprojectTimeblocks(subprojectId, payload);
      }
      try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'subproject_timeblocks_saved' } })); } catch (_) {}
    } else {
      // creation flow: persist blocks in wizard state
      setBlocks(subprojectIndex, blocksState);
    }
    onClose && onClose();
  };

  return (
    <div className="modal-content create-ticket-modal" style={{ maxWidth: 650 }}>
        <div className="modal-header">
          <div className="modal-title" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Ticket size={24} />
            <h3 style={{ marginLeft: 8 }}>{t('projectWizard.subTimeblocks.heading', 'Zeitblöcke für {{name}}', { name: subprojectName })}</h3>
          </div>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        <div className="form-body">
          {loading ? (
            <div style={{ padding: 12 }}>{t('common.loading', 'Lade...')}</div>
          ) : (
            <div className="card" style={{ padding: 12 }}>
              {blocksState.map((tb, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    marginBottom: 12,
                    background: 'linear-gradient(135deg, var(--color-bg) 0%, var(--color-border-light) 100%)',
                    borderRadius: 8,
                    border: '1px solid var(--color-border-light)',
                    borderLeft: '3px solid var(--color-primary-legacy)',
                    boxShadow: '0 1px 4px var(--color-shadow-05)',
                    padding: 12
                  }}
                >
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('projectWizard.timeblock.titleLabel', 'Zeitblock Titel')}</label>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <input type="text" className="form-input" placeholder={t('projectWizard.timeblock.titlePlaceholder', 'Zeitblock Titel')} value={tb.titleSuffix} onChange={(e) => updateAt(idx, { titleSuffix: e.target.value })} />
                      <CalenderMarkerHeaderSelect
                        value={tb.calenderMarker || ''}
                        onChange={(value) => updateAt(idx, { calenderMarker: value })}
                      />
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('projectWizard.timeblock.descriptionLabel', 'Zeitblock Beschreibung')}</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        title={t('projectWizard.timeblock.checklist', 'Checkliste')} aria-label={t('projectWizard.timeblock.checklist', 'Checkliste')}
                        onClick={() => updateAt(idx, { showChecklist: !tb.showChecklist })}
                        style={{ alignSelf: 'flex-start', marginTop: 2, background: tb.showChecklist ? 'var(--color-primary)' : undefined, color: tb.showChecklist ? 'var(--color-text-inverse)' : undefined }}
                      >
                        <ListChecks size={16} />
                      </button>
                      <textarea className="form-textarea" rows="4" placeholder={t('projectWizard.placeholders.description', 'Beschreibung')} value={tb.description} onChange={(e) => updateAt(idx, { description: e.target.value })} />
                    </div>
                    {tb.showChecklist && (
                      <TimeblockChecklist
                        items={tb.checklist || []}
                        onChange={(next) => updateAt(idx, { checklist: next })}
                      />
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <select className="form-select" value={tb.assignedTo} onChange={(e) => updateAt(idx, { assignedTo: e.target.value })} style={{ maxWidth: 220 }} required>
                      <option value="">{t('projectWizard.placeholders.assignedTo', 'Zugewiesen an...')}</option>
                      {users.map(u => (
                        <option key={u.email} value={u.email}>{u.displayName || u.email}</option>
                      ))}
                    </select>
                    <input type="number" className="form-input" value={tb.plannedHours || ''} onChange={(e) => {
                      const v = e.target.value;
                      let nextVal = v;
                      if (v !== '') {
                        const num = Math.max(0, Math.min(12, parseFloat(v)));
                        nextVal = Number.isNaN(num) ? '' : String(num);
                      }
                      updateAt(idx, { plannedHours: nextVal });
                    }} step={0.25} min={0} max={12} placeholder="Std." style={{ maxWidth: 90 }} />
                    <button type="button" className="btn btn-link" style={{ color: 'var(--color-text-muted)', minWidth: 40 }} title={t('projectWizard.timeblock.delete', 'Zeitblock löschen')} onClick={() => removeAt(idx)}>{t('projectWizard.timeblock.delete', 'Zeitblock löschen')}</button>
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => insertAt(blocksState.length - 1)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Plus size={16} /> {t('projectWizard.buttons.addAnotherTimeblock', 'Weiterer Zeitblock')}
              </button>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.close', 'Schließen')}</button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>{t('common.save', 'Speichern')}</button>
        </div>
    </div>
  );
};

export default CreateProjectWizard;




